/**
 * ============================================================================
 *  CSOperationsDashboard.jsx  —  Operations Dashboard (Tab 1 of mockup)
 * ----------------------------------------------------------------------------
 *  Replaces the previous Material-UI version. Renders the mockup-styled
 *  layout exactly as designed:
 *
 *      • 5 KPI cards (Stock CS-1/CS-2 split · Out for Processing · Pending
 *        Approval · Today's Throughput · Excess Returns)
 *      • Inward Activity panel — Primary vs Compared-with day
 *      • Outward Activity panel — Primary vs Compared-with day
 *      • Drill-down table — Product → Brand → Grade → Pack Size
 *      • Trend Analysis — line + bar charts + 6 totals tiles
 *
 *  Page-level chrome (the "Cold Store Operations" header, shift toggle, theme
 *  switch, sub-tab nav) lives in OperationsContainer.jsx — this file accepts
 *  the chosen `shift` as a prop and renders only the inner page content.
 *
 *  Backend: api/cs_ops_api.php  (5 actions: kpis, inward, outward, drilldown, trend)
 *  CSS:     ./CSOperationsDashboard.css  (all classes prefixed `csops-`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  fetchCSOpsKpis,
  fetchCSOpsInward,
  fetchCSOpsOutward,
  fetchCSOpsDrilldown,
  fetchCSOpsTrend,
  fetchCSOpsStockPallets,
  todayISO,
  yesterdayISO,
  prevDay,
} from '../utils/csOpsApi';
import { exportPalletList, PALLET_EXPORT_COLUMNS } from '../utils/csOpsExport';
import './CSOperationsDashboard.css';

/* ---------------------------------------------------------------------------
 *  Formatters
 * ------------------------------------------------------------------------- */
const fmtN = (n) => Number(n || 0).toLocaleString();
const fmtK = (n) => (Number(n || 0) / 1000).toFixed(1) + 'k';

/* ---------------------------------------------------------------------------
 *  Per-section period selector
 * ------------------------------------------------------------------------- */
function PeriodSelector({ value, onChange, date, onDateChange }) {
  const options = ['Today', 'Yesterday', 'Week', 'Month'];
  return (
    <div className="csops-section-period">
      {options.map(opt => (
        <button key={opt}
          className={value === opt ? 'active' : ''}
          onClick={() => onChange(opt)}>
          {opt}
        </button>
      ))}
      <input type="date" value={date} onChange={e => onDateChange(e.target.value)} />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 *  Stock On Hand split KPI card (CS-1 / CS-2)
 * ------------------------------------------------------------------------- */
function StockOnHandSplitCard({ data, loading }) {
  if (loading || !data) {
    return (
      <div className="csops-kpi-card csops-blue">
        <div className="csops-kpi-label"><span>📦 STOCK ON HAND</span></div>
        <div className="csops-kpi-skeleton">Loading…</div>
      </div>
    );
  }
  const { cs1, cs2 } = data;
  const pct1 = cs1.bins_total > 0 ? Math.round((cs1.bins_used / cs1.bins_total) * 100) : 0;
  const pct2 = cs2.bins_total > 0 ? Math.round((cs2.bins_used / cs2.bins_total) * 100) : 0;
  const totalC = cs1.cartons + cs2.cartons;
  const totalP = cs1.pallets + cs2.pallets;
  const totalK = cs1.kgs + cs2.kgs;
  return (
    <div className="csops-kpi-card csops-blue">
      <div className="csops-kpi-label"><span>📦 STOCK ON HAND</span></div>
      <div className="csops-cs-row" style={{ marginTop: 10 }}>
        <div className="csops-cs-header">
          <span className="csops-cs-name">❄️ CS-1</span>
          <span className="csops-cs-utilization csops-mono">{pct1}% · {cs1.bins_used}/{cs1.bins_total} bins</span>
        </div>
        <div className="csops-cs-value-row">
          <span className="csops-cs-value">{fmtN(cs1.cartons)}</span>
          <span className="csops-cs-unit">cartons</span>
        </div>
        <div className="csops-cs-meta">{cs1.pallets} pal · {fmtK(cs1.kgs)} kg</div>
        <div className="csops-util-bar"><div style={{ width: pct1 + '%', background: '#22c55e' }}></div></div>
      </div>
      <div className="csops-cs-row">
        <div className="csops-cs-header">
          <span className="csops-cs-name">❄️ CS-2</span>
          <span className="csops-cs-utilization csops-mono">{pct2}% · {cs2.bins_used}/{cs2.bins_total} bins</span>
        </div>
        <div className="csops-cs-value-row">
          <span className="csops-cs-value">{fmtN(cs2.cartons)}</span>
          <span className="csops-cs-unit">cartons</span>
        </div>
        <div className="csops-cs-meta">{cs2.pallets} pal · {fmtK(cs2.kgs)} kg</div>
        <div className="csops-util-bar"><div style={{ width: pct2 + '%', background: '#60a5fa' }}></div></div>
      </div>
      <div className="csops-total-strip">
        <div>
          <div className="csops-total-label">Total</div>
          <div className="csops-total-meta">{totalP} pal · {fmtK(totalK)} kg</div>
        </div>
        <div className="csops-total-value">{fmtN(totalC)}</div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 *  Simple KPI card
 * ------------------------------------------------------------------------- */
function KpiCard({ color, icon, label, value, subtitle, loading }) {
  return (
    <div className={'csops-kpi-card csops-' + color}>
      <div className="csops-kpi-label"><span>{icon} {label}</span></div>
      <div className="csops-kpi-value">{loading ? '…' : value}</div>
      <div className="csops-kpi-subtitle">{loading ? '\u00A0' : subtitle}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 *  Activity panel (used for Inward Primary, Inward Compare, Outward Primary, Outward Compare)
 * ------------------------------------------------------------------------- */
function ActivityPanel({ heading, rows, totalLabel, groups, loading }) {
  const safeRows = rows || [];
  const total = safeRows.reduce((acc, r) => ({
    cartons: acc.cartons + (r.cartons || 0),
    pallets: acc.pallets + (r.pallets || 0),
    kgs:     acc.kgs     + (r.kgs     || 0),
  }), { cartons: 0, pallets: 0, kgs: 0 });

  return (
    <div className="csops-activity-panel">
      <div className={'csops-activity-head ' + heading.kind}>{heading.label}</div>
      {loading ? (
        <div className="csops-activity-row"><span className="label">Loading…</span></div>
      ) : groups.map(g => (
        <React.Fragment key={g.title}>
          <div className="csops-activity-row csops-divider">{g.title}</div>
          {g.indices.map(i => (
            <div className="csops-activity-row" key={i}>
              <span className="label">{safeRows[i]?.type || '—'}</span>
              <span className="value">{fmtN(safeRows[i]?.cartons)}</span>
              <span className="meta">{safeRows[i]?.pallets || 0} pal · {fmtK(safeRows[i]?.kgs)} kg</span>
            </div>
          ))}
        </React.Fragment>
      ))}
      <div className="csops-activity-row csops-total">
        <span className="label">{totalLabel}</span>
        <span className="value">{fmtN(total.cartons)}</span>
        <span className="meta">{total.pallets} pal · {fmtK(total.kgs)} kg</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 *  Drill-down table — Product → Brand → Grade → Pack Size
 * ------------------------------------------------------------------------- */
function DrilldownTable({ data, loading, onRowEnter, onRowLeave }) {
  const rows = data || [];
  const [exp, setExp] = useState({ products: {}, brands: {}, grades: {} });

  // Auto-expand first product on first data load
  useEffect(() => {
    if (rows.length > 0 && Object.keys(exp.products).length === 0) {
      setExp(p => ({ ...p, products: { [rows[0].name]: true } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const grandTotal = rows.reduce((s, p) => s + (p.cartons || 0), 0);
  const togP = n => setExp(p => ({ ...p, products: { ...p.products, [n]: !p.products[n] } }));
  const togB = k => setExp(p => ({ ...p, brands:   { ...p.brands,   [k]: !p.brands[k]   } }));
  const togG = k => setExp(p => ({ ...p, grades:   { ...p.grades,   [k]: !p.grades[k]   } }));

  if (loading) return <div className="csops-drilldown-empty">Loading hierarchy…</div>;
  if (rows.length === 0) return <div className="csops-drilldown-empty">No production in this period.</div>;

  return (
    <table className="csops-drilldown-table">
      <thead>
        <tr>
          <th className="rank">#</th>
          <th>PRODUCT / BRAND / GRADE / PACK SIZE</th>
          <th className="distribution">DISTRIBUTION</th>
          <th className="num">CARTONS</th>
          <th className="num">LBS</th>
          <th className="pct">%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((product, pIdx) => {
          const pPct = grandTotal > 0 ? Math.round((product.cartons / grandTotal) * 100) : 0;
          const pExp = !!exp.products[product.name];
          const hasBrands = (product.brands || []).length > 0;
          return (
            <React.Fragment key={pIdx}>
              <tr className="csops-drilldown-row lvl-1"
                  onClick={() => hasBrands && togP(product.name)}
                  onMouseEnter={() => onRowEnter && onRowEnter({ product: product.name })}
                  onMouseLeave={() => onRowLeave && onRowLeave()}>
                <td className="rank">{pIdx + 1}</td>
                <td className="indent-1">
                  {hasBrands && <span className={'csops-row-caret ' + (pExp ? 'expanded' : '')}>{pExp ? '▼' : '▶'}</span>}
                  <span className="csops-row-name lvl-1">{product.name}</span>
                  {hasBrands && <span className="csops-row-meta">({(product.brands || []).length} brands)</span>}
                </td>
                <td><div className="csops-dist-bar-bg"><div className="csops-dist-bar lvl-1" style={{ width: pPct + '%' }}></div></div></td>
                <td className="num csops-drilldown-num lvl-1">{fmtN(product.cartons)}</td>
                <td className="lbs-cell">{fmtN(product.lbs)}</td>
                <td className="pct-cell">{pPct}%</td>
              </tr>
              {pExp && (product.brands || []).map((brand, bIdx) => {
                const bKey = product.name + '-' + brand.name;
                const bPct = product.cartons > 0 ? Math.round((brand.cartons / product.cartons) * 100) : 0;
                const bExp = !!exp.brands[bKey];
                const hasGrades = (brand.grades || []).length > 0;
                return (
                  <React.Fragment key={bIdx}>
                    <tr className="csops-drilldown-row lvl-2"
                        onClick={(e) => { e.stopPropagation(); hasGrades && togB(bKey); }}
                        onMouseEnter={(e) => { e.stopPropagation(); onRowEnter && onRowEnter({ product: product.name, brand: brand.name }); }}
                        onMouseLeave={() => onRowLeave && onRowLeave()}>
                      <td className="rank"></td>
                      <td className="indent-2">
                        {hasGrades && <span className={'csops-row-caret ' + (bExp ? 'expanded' : '')}>{bExp ? '▼' : '▶'}</span>}
                        <span className="csops-row-name lvl-2">↳ {brand.name}</span>
                        {hasGrades && <span className="csops-row-meta">({(brand.grades || []).length})</span>}
                      </td>
                      <td><div className="csops-dist-bar-bg"><div className="csops-dist-bar lvl-2" style={{ width: bPct + '%' }}></div></div></td>
                      <td className="num csops-drilldown-num lvl-2">{fmtN(brand.cartons)}</td>
                      <td className="lbs-cell">{fmtN(brand.lbs)}</td>
                      <td className="pct-cell">{bPct}%</td>
                    </tr>
                    {bExp && (brand.grades || []).map((grade, gIdx) => {
                      const gKey = bKey + '-' + grade.name;
                      const gPct = brand.cartons > 0 ? Math.round((grade.cartons / brand.cartons) * 100) : 0;
                      const gExp = !!exp.grades[gKey];
                      const hasPs = (grade.pack_sizes || []).length > 0;
                      return (
                        <React.Fragment key={gIdx}>
                          <tr className="csops-drilldown-row lvl-3"
                              onClick={(e) => { e.stopPropagation(); hasPs && togG(gKey); }}
                              onMouseEnter={(e) => { e.stopPropagation(); onRowEnter && onRowEnter({ product: product.name, brand: brand.name, grade: grade.name }); }}
                              onMouseLeave={() => onRowLeave && onRowLeave()}>
                            <td className="rank"></td>
                            <td className="indent-3">
                              {hasPs && <span className={'csops-row-caret ' + (gExp ? 'expanded' : '')}>{gExp ? '▼' : '▶'}</span>}
                              <span className="csops-row-name lvl-3">↳ {grade.name}</span>
                              {hasPs && <span className="csops-row-meta">({(grade.pack_sizes || []).length})</span>}
                            </td>
                            <td><div className="csops-dist-bar-bg"><div className="csops-dist-bar lvl-3" style={{ width: gPct + '%' }}></div></div></td>
                            <td className="num csops-drilldown-num lvl-3">{fmtN(grade.cartons)}</td>
                            <td className="lbs-cell">{fmtN(grade.lbs)}</td>
                            <td className="pct-cell">{gPct}%</td>
                          </tr>
                          {gExp && (grade.pack_sizes || []).map((ps, psIdx) => {
                            const psPct = grade.cartons > 0 ? Math.round((ps.cartons / grade.cartons) * 100) : 0;
                            return (
                              <tr className="csops-drilldown-row lvl-4" key={psIdx}
                                  onMouseEnter={(e) => { e.stopPropagation(); onRowEnter && onRowEnter({ product: product.name, brand: brand.name, grade: grade.name, pack_size: ps.name }); }}
                                  onMouseLeave={() => onRowLeave && onRowLeave()}>
                                <td className="rank"></td>
                                <td className="indent-4">
                                  <span className="csops-row-name lvl-4">↳ {ps.name}</span>
                                </td>
                                <td><div className="csops-dist-bar-bg"><div className="csops-dist-bar lvl-4" style={{ width: psPct + '%' }}></div></div></td>
                                <td className="num csops-drilldown-num lvl-4">{fmtN(ps.cartons)}</td>
                                <td className="lbs-cell">{fmtN(ps.lbs)}</td>
                                <td className="pct-cell">{psPct}%</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/* ---------------------------------------------------------------------------
 *  Trend charts (data-driven SVG)
 * ------------------------------------------------------------------------- */
function TrendCharts({ series }) {
  const safe = series || [];
  if (safe.length === 0) {
    return <div className="csops-trend-empty">No trend data for this period.</div>;
  }

  const W = 800, H = 200, padL = 40, padR = 20, padT = 20, padB = 20;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxVal = Math.max(1, ...safe.map(d => Math.max(
    d.produced, d.repack_in, d.repack_out, d.reprint_out, d.reproc_out, d.shipped
  )));
  const xStep = safe.length > 1 ? innerW / (safe.length - 1) : 0;
  const xBar  = innerW / Math.max(safe.length, 1);

  const pt = (i, v) => [padL + i * xStep, padT + innerH - (v / maxVal) * innerH];
  const producedPoints = safe.map((d, i) => pt(i, d.produced));

  return (
    <>
      <div className="csops-chart-panel csops-line">
        <div className="csops-chart-title csops-line">📉 Daily Trend · Produced (cartons)</div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 200 }}>
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <line key={i} x1={padL} y1={padT + p * innerH} x2={W - padR} y2={padT + p * innerH}
                  stroke={i === 4 ? '#cbd5e1' : '#f1f5f9'} />
          ))}
          <polyline points={producedPoints.map(p => p.join(',')).join(' ')}
                    fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
          {producedPoints.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="4" fill="#16a34a" />)}
          {safe.map((d, i) => (
            <text key={i} x={padL + i * xStep} y={H - 4} fontSize="9" textAnchor="middle" fill="#94a3b8">
              {d.date.slice(5)}
            </text>
          ))}
        </svg>
      </div>
      <div className="csops-chart-panel csops-bar">
        <div className="csops-chart-title csops-bar">📊 Daily Breakdown · Produced (cartons)</div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 200 }}>
          <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="#cbd5e1" />
          {safe.map((d, i) => {
            const h = (d.produced / maxVal) * innerH;
            return (
              <rect key={i}
                    x={padL + i * xBar + xBar * 0.15}
                    y={padT + innerH - h}
                    width={xBar * 0.7}
                    height={h}
                    fill="#16a34a" rx="3" />
            );
          })}
        </svg>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------
 *  Pallet tooltip — floating panel showing pallet-level breakdown for the
 *  currently-hovered drilldown row. Fixed-position on the right side of the
 *  viewport so it doesn't chase the cursor or overlap the table.
 * ------------------------------------------------------------------------- */
function PalletTooltip({ path, data, loading, asOfDate, onMouseEnter, onMouseLeave, onClose }) {
  const pallets = data?.pallets || [];
  const totals  = data?.totals  || { cartons: 0, kgs: 0, pallet_count: 0 };
  const pathParts = [path.product, path.brand, path.grade, path.pack_size].filter(Boolean);

  const canExport = pallets.length > 0;
  const handleExport = async () => {
    if (!canExport) return;
    const slug = pathParts.join('_').replace(/[^A-Za-z0-9_-]+/g, '_') || 'all';
    await exportPalletList({
      filename:   `pallets_${slug}_asof_${asOfDate}`,
      sheetName:  'Pallets',
      rows:       pallets,
      columns:    PALLET_EXPORT_COLUMNS,
      summaryRows: [
        ['CSOps · Drilldown · Pallet Export'],
        ['Path',  pathParts.join(' > ') || '(all)'],
        ['As of', asOfDate],
        ['Pallets', totals.pallet_count, 'Cartons', totals.cartons, 'Kgs', totals.kgs],
        ['Generated', new Date().toLocaleString()],
        [],
      ],
    });
  };

  return (
    <div className="csops-pallet-tooltip"
         onMouseEnter={onMouseEnter}
         onMouseLeave={onMouseLeave}>
      <div className="csops-pallet-tooltip-header">
        <div className="csops-pallet-tooltip-path">
          {pathParts.join(' › ') || '(all)'}
        </div>
        <div className="csops-pallet-tooltip-actions">
          <button className="csops-pallet-export-btn"
                  onClick={handleExport}
                  disabled={!canExport}
                  title={canExport ? 'Download these pallets as Excel' : 'No pallets to export'}>
            📥 Excel
          </button>
          <button className="csops-pallet-close-btn"
                  onClick={onClose}
                  title="Close (Esc)">✕</button>
        </div>
        {!loading && (
          <div className="csops-pallet-tooltip-totals">
            <span>{fmtN(totals.pallet_count)} pal</span>
            <span>·</span>
            <span>{fmtN(totals.cartons)} ctn</span>
            <span>·</span>
            <span>{fmtN(totals.kgs)} kg</span>
            {data?.truncated && <span className="csops-pallet-tooltip-trunc">(top {data.limit}, more exist)</span>}
          </div>
        )}
      </div>
      {loading && pallets.length === 0 ? (
        <div className="csops-pallet-tooltip-loading">Loading pallets…</div>
      ) : pallets.length === 0 ? (
        <div className="csops-pallet-tooltip-empty">No pallets match this filter.</div>
      ) : (
        <div className="csops-pallet-tooltip-tablewrap">
          <table className="csops-pallet-tooltip-table">
            <thead>
              <tr>
                <th>PALLET ID</th>
                <th>CS</th>
                <th>BIN</th>
                <th className="num">CTN</th>
                <th className="num">KG</th>
                <th className="num">LB</th>
                <th>PRODUCT ID</th>
                <th>DAY CODE</th>
                <th>LOT NO</th>
                <th>TRACE ID</th>
                <th>PROD DATE</th>
              </tr>
            </thead>
            <tbody>
              {pallets.map((p, i) => (
                <tr key={p.pallet_id || (p.bin + '-' + i)}>
                  <td className="mono">{p.pallet_id || '—'}</td>
                  <td className={'csops-pallet-cs ' + (p.cs === 'CS-1' ? 'cs1' : p.cs === 'CS-2' ? 'cs2' : '')}>
                    {p.cs}
                  </td>
                  <td className="mono">{p.bin || '—'}</td>
                  <td className="num mono">{fmtN(p.cartons)}</td>
                  <td className="num mono">{fmtN(p.kgs)}</td>
                  <td className="num mono">{fmtN(p.lbs)}</td>
                  <td className="mono">{p.product_id || '—'}</td>
                  <td className="mono">{p.day_code || '—'}</td>
                  <td className="mono">{p.lot_no || '—'}</td>
                  <td className="mono">{p.trace_id || '—'}</td>
                  <td className="mono">{p.production_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 *  Helper: snap section date when period preset is clicked
 * ------------------------------------------------------------------------- */
function applyPeriodToDate(period, setter) {
  if (period === 'Today')     return setter(todayISO());
  if (period === 'Yesterday') return setter(yesterdayISO());
  // Week and Month anchor on today
  return setter(todayISO());
}

/* ============================================================================
 *  MAIN COMPONENT
 * ============================================================================ */
export default function CSOperationsDashboard({ shift = 'ALL' }) {
  // ---- Top-level "as-of date" selector (drives KPIs + Drilldown) ----
  const [asOfMode, setAsOfMode] = useState('Today');   // 'Today' | 'Yesterday' | 'Custom'
  const [asOfDate, setAsOfDate] = useState(todayISO());

  // ---- Per-section state (Inward / Outward use ACTIVITY date, Trend uses period) ----
  const [inwardPeriod,  setInwardPeriod]  = useState('Yesterday');
  const [inwardDate,    setInwardDate]    = useState(yesterdayISO());
  const [outwardPeriod, setOutwardPeriod] = useState('Today');
  const [outwardDate,   setOutwardDate]   = useState(todayISO());
  const [trendPeriod,   setTrendPeriod]   = useState('Week');
  const [trendDate,     setTrendDate]     = useState(todayISO());

  // ---- Data state ----
  const [kpis,            setKpis]            = useState(null);
  const [inward,          setInward]          = useState(null);
  const [inwardCompare,   setInwardCompare]   = useState(null);
  const [outward,         setOutward]         = useState(null);
  const [outwardCompare,  setOutwardCompare]  = useState(null);
  const [drilldown,       setDrilldown]       = useState(null);
  const [trend,           setTrend]           = useState(null);

  // ---- Loading + error tracking ----
  const [loading, setLoading] = useState({});
  const [errors,  setErrors]  = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  // ---- Drilldown pallet hover state ----
  const [hoveredPath,   setHoveredPath]   = useState(null);
  const [palletCache,   setPalletCache]   = useState({});   // key → response data
  const [palletLoading, setPalletLoading] = useState(false);
  const hoverTimerRef    = useRef(null);
  const lastFetchKeyRef  = useRef(null);

  const pathKey = (p) =>
    `${p.product||''}|${p.brand||''}|${p.grade||''}|${p.pack_size||''}`;

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const callSection = useCallback(async (key, fn) => {
    setLoading(l => ({ ...l, [key]: true }));
    setErrors(e => ({ ...e, [key]: null }));
    try {
      const result = await fn();
      if (!mountedRef.current) return null;
      if (result?.success) return result.data;
      setErrors(e => ({ ...e, [key]: result?.error || 'Failed' }));
      return null;
    } catch (err) {
      if (mountedRef.current) setErrors(e => ({ ...e, [key]: err.message }));
      return null;
    } finally {
      if (mountedRef.current) setLoading(l => ({ ...l, [key]: false }));
    }
  }, []);

  // ---- Loaders, one per section ----
  const loadKpis = useCallback(async () => {
    const d = await callSection('kpis', () => fetchCSOpsKpis(asOfDate, shift));
    if (d) { setKpis(d); setLastUpdated(new Date()); }
  }, [callSection, asOfDate, shift]);

  const loadInward = useCallback(async () => {
    const [primary, compare] = await Promise.all([
      callSection('inward',         () => fetchCSOpsInward(inwardDate,           shift)),
      callSection('inward_compare', () => fetchCSOpsInward(prevDay(inwardDate),  shift)),
    ]);
    if (primary) setInward(primary);
    if (compare) setInwardCompare(compare);
  }, [callSection, inwardDate, shift]);

  const loadOutward = useCallback(async () => {
    const [primary, compare] = await Promise.all([
      callSection('outward',         () => fetchCSOpsOutward(outwardDate,           shift)),
      callSection('outward_compare', () => fetchCSOpsOutward(prevDay(outwardDate),  shift)),
    ]);
    if (primary) setOutward(primary);
    if (compare) setOutwardCompare(compare);
  }, [callSection, outwardDate, shift]);

  const loadDrilldown = useCallback(async () => {
    const d = await callSection('drilldown', () => fetchCSOpsDrilldown(asOfDate, shift));
    if (d) setDrilldown(d);
  }, [callSection, asOfDate, shift]);

  const loadTrend = useCallback(async () => {
    const d = await callSection('trend', () => fetchCSOpsTrend(trendDate, trendPeriod, shift));
    if (d) setTrend(d);
  }, [callSection, trendDate, trendPeriod, shift]);

  // ---- Effects: reload on filter change ----
  useEffect(() => { loadKpis(); },      [loadKpis]);
  useEffect(() => { loadInward(); },    [loadInward]);
  useEffect(() => { loadOutward(); },   [loadOutward]);
  useEffect(() => { loadDrilldown(); }, [loadDrilldown]);
  useEffect(() => { loadTrend(); },     [loadTrend]);

  // ---- Auto-refresh KPIs every 60s ----
  useEffect(() => {
    const id = setInterval(loadKpis, 60_000);
    return () => clearInterval(id);
  }, [loadKpis]);

  // ---- Manual refresh ----
  const refreshAll = () => {
    loadKpis();
    loadInward();
    loadOutward();
    loadDrilldown();
    loadTrend();
  };

  // ---- Drilldown row hover: fetch pallet details (debounced + cached) ----
  const handleRowEnter = useCallback((path) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      setHoveredPath(path);
      const key = pathKey(path);
      if (palletCache[key]) return;     // already cached
      setPalletLoading(true);
      lastFetchKeyRef.current = key;
      try {
        const res = await fetchCSOpsStockPallets(asOfDate, path);
        if (!mountedRef.current) return;
        if (lastFetchKeyRef.current !== key) return;  // user moved on
        if (res?.success) {
          setPalletCache(p => ({ ...p, [key]: res.data }));
        }
      } finally {
        if (mountedRef.current) setPalletLoading(false);
      }
    }, 200);
  }, [asOfDate, palletCache]);

  const handleRowLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    // Brief delay so the tooltip doesn't flicker when moving between rows
    hoverTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setHoveredPath(null);
    }, 120);
  }, []);

  // Invalidate cache when as-of date changes (pallet contents differ per date)
  useEffect(() => {
    setPalletCache({});
    setHoveredPath(null);
  }, [asOfDate]);

  // Esc closes the pallet tooltip
  useEffect(() => {
    if (!hoveredPath) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        setHoveredPath(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hoveredPath]);

  // ---- Group layout for Inward/Outward panels ----
  const inwardGroups  = useMemo(() => [
    { title: 'Production Inflow',  indices: [0]            },
    { title: 'Cold Store Returns', indices: [1, 2, 3, 4, 5] },
  ], []);
  const outwardGroups = useMemo(() => [
    { title: 'Processing Outflow', indices: [0, 1, 2, 3] },
    { title: 'Shipment Outflow',   indices: [4]          },
  ], []);

  // ---- KPI subtitles (derived) ----
  const outSub = kpis?.out_for_processing
    ? `RPK ${fmtN(kpis.out_for_processing.repack_out)} · RPT ${fmtN(kpis.out_for_processing.reprint_out)} · RPS ${fmtN(kpis.out_for_processing.reproc_out)} · QC ${fmtN(kpis.out_for_processing.qc_sample)}`
    : '';
  const pendSub = kpis?.pending_approval
    ? `${kpis.pending_approval.critical} critical · ${kpis.pending_approval.routine} routine`
    : '';
  const thruSub = kpis?.todays_throughput
    ? `${fmtN(kpis.todays_throughput.in)} IN · ${fmtN(kpis.todays_throughput.out)} OUT (cartons)`
    : '';
  const excessSub = kpis?.excess_returns
    ? (kpis.excess_returns.count > 0
        ? `oldest ${kpis.excess_returns.oldest_days}d · review pending`
        : 'None pending')
    : '';

  const anyError = Object.values(errors).find(Boolean);

  // ---- As-of date controls ----
  const setAsOfPreset = (mode) => {
    setAsOfMode(mode);
    if (mode === 'Today')     setAsOfDate(todayISO());
    if (mode === 'Yesterday') setAsOfDate(yesterdayISO());
    // 'Custom' leaves the date input as the source of truth
  };
  const handleAsOfDateChange = (newDate) => {
    setAsOfDate(newDate);
    // If the picked date matches a preset, sync the chip; otherwise mark as Custom.
    if (newDate === todayISO())          setAsOfMode('Today');
    else if (newDate === yesterdayISO()) setAsOfMode('Yesterday');
    else                                  setAsOfMode('Custom');
  };

  return (
    <div className="csops-root">
      {/* Inner page title (the outer container owns the master "Cold Store Operations" h1) */}
      <div className="csops-page-title-bar">
        <div>
          <h2>📊 Cold Store Operations Dashboard</h2>
          <div className="csops-meta">
            Shift: <strong>{shift}</strong> · As of <strong>{asOfDate}</strong> · Auto-refresh every 60s
            {lastUpdated && ` · Last updated ${lastUpdated.toLocaleTimeString()}`}
          </div>
        </div>
        <div className="csops-title-controls">
          {/* As-of preset chips */}
          <div className="csops-asof-toggle">
            {['Today', 'Yesterday', 'Custom'].map(opt => (
              <button key={opt}
                className={asOfMode === opt ? 'active' : ''}
                onClick={() => setAsOfPreset(opt)}>
                {opt}
              </button>
            ))}
            <input type="date" value={asOfDate}
                   onChange={e => handleAsOfDateChange(e.target.value)} />
          </div>
          <button className="csops-icon-btn" onClick={refreshAll}>↻ Refresh</button>
        </div>
      </div>

      {/* Error banner */}
      {anyError && (
        <div className="csops-error-banner">
          ⚠ Some sections failed to load: <span className="csops-mono">{anyError}</span>
        </div>
      )}

      {/* KPI ROW */}
      <div className="csops-kpi-row">
        <StockOnHandSplitCard data={kpis?.stock_on_hand} loading={loading.kpis && !kpis} />
        <KpiCard color="amber"  icon="🚚" label="OUT FOR PROCESSING"
                 value={fmtN(kpis?.out_for_processing?.total)} subtitle={outSub}
                 loading={loading.kpis && !kpis} />
        <KpiCard color="violet" icon="⏳" label="PENDING APPROVAL"
                 value={fmtN(kpis?.pending_approval?.total)} subtitle={pendSub}
                 loading={loading.kpis && !kpis} />
        <KpiCard color="green"  icon="📈" label="TODAY'S THROUGHPUT"
                 value={fmtN(kpis?.todays_throughput?.in)} subtitle={thruSub}
                 loading={loading.kpis && !kpis} />
        <KpiCard color="red"    icon="⚠"  label="EXCESS RETURNS"
                 value={fmtN(kpis?.excess_returns?.count)} subtitle={excessSub}
                 loading={loading.kpis && !kpis} />
      </div>

      {/* INWARD ACTIVITY */}
      <div className="csops-section csops-s-green">
        <div className="csops-section-header">
          <div className="csops-section-title">📥 Inward Activity · Pallets &amp; Cartons Coming Into CS</div>
          <div className="csops-section-controls">
            <PeriodSelector value={inwardPeriod}
                            onChange={p => { setInwardPeriod(p); applyPeriodToDate(p, setInwardDate); }}
                            date={inwardDate}
                            onDateChange={setInwardDate} />
          </div>
        </div>
        <div className="csops-activity-grid">
          <ActivityPanel
            heading={{ kind: 'primary', label: '🟢 PRIMARY · ' + inwardDate }}
            rows={inward?.rows} totalLabel="TOTAL IN" groups={inwardGroups}
            loading={loading.inward && !inward} />
          <ActivityPanel
            heading={{ kind: 'compare', label: '⏰ COMPARED WITH · ' + prevDay(inwardDate) }}
            rows={inwardCompare?.rows} totalLabel="TOTAL IN" groups={inwardGroups}
            loading={loading.inward_compare && !inwardCompare} />
        </div>
      </div>

      {/* OUTWARD ACTIVITY */}
      <div className="csops-section csops-s-orange">
        <div className="csops-section-header">
          <div className="csops-section-title">📤 Outward Activity · Pallets &amp; Cartons Leaving CS</div>
          <div className="csops-section-controls">
            <PeriodSelector value={outwardPeriod}
                            onChange={p => { setOutwardPeriod(p); applyPeriodToDate(p, setOutwardDate); }}
                            date={outwardDate}
                            onDateChange={setOutwardDate} />
          </div>
        </div>
        <div className="csops-activity-grid">
          <ActivityPanel
            heading={{ kind: 'primary', label: '🟢 PRIMARY · ' + outwardDate }}
            rows={outward?.rows} totalLabel="TOTAL OUT" groups={outwardGroups}
            loading={loading.outward && !outward} />
          <ActivityPanel
            heading={{ kind: 'compare', label: '⏰ COMPARED WITH · ' + prevDay(outwardDate) }}
            rows={outwardCompare?.rows} totalLabel="TOTAL OUT" groups={outwardGroups}
            loading={loading.outward_compare && !outwardCompare} />
        </div>
      </div>

      {/* DRILL-DOWN  —  follows the page-level As-of date (mirrored here for convenience) */}
      <div className="csops-drilldown-section">
        <div className="csops-drilldown-header">
          <div className="csops-drilldown-title">
            📊 PRODUCT → BRAND → GRADE → PACK SIZE  ·  STOCK AS OF {asOfDate}
          </div>
          <div className="csops-drilldown-controls">
            <div className="csops-drilldown-totals">
              {fmtN(drilldown?.totals?.cartons)} CARTONS
              <span className="meta">{fmtN(drilldown?.totals?.lbs)} LBS</span>
            </div>
            <div className="csops-drilldown-asof">
              {['Today', 'Yesterday'].map(opt => (
                <button key={opt}
                  className={asOfMode === opt ? 'active' : ''}
                  onClick={() => setAsOfPreset(opt)}>{opt}</button>
              ))}
              <input type="date" value={asOfDate}
                     onChange={e => handleAsOfDateChange(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="csops-drilldown-hint">
          💡 Hover any row to see its pallet-level breakdown (CS, bin, pallet ID, cartons, weight)
        </div>
        <DrilldownTable
          data={drilldown?.hierarchy}
          loading={loading.drilldown && !drilldown}
          onRowEnter={handleRowEnter}
          onRowLeave={handleRowLeave} />
      </div>

      {/* Floating pallet detail tooltip (drilldown row hover) */}
      {hoveredPath && (
        <PalletTooltip
          path={hoveredPath}
          data={palletCache[pathKey(hoveredPath)]}
          loading={palletLoading && !palletCache[pathKey(hoveredPath)]}
          asOfDate={asOfDate}
          onMouseEnter={() => {
            // Cancel pending hide so the tooltip stays while the user is over it
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
          }}
          onMouseLeave={handleRowLeave}
          onClose={() => {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
            setHoveredPath(null);
          }} />
      )}

      {/* TREND */}
      <div className="csops-section csops-s-purple">
        <div className="csops-section-header">
          <div className="csops-section-title">📈 Trend Analysis · Production · Returns · Outflow</div>
          <div className="csops-section-controls">
            <PeriodSelector value={trendPeriod}
                            onChange={p => { setTrendPeriod(p); applyPeriodToDate(p, setTrendDate); }}
                            date={trendDate}
                            onDateChange={setTrendDate} />
          </div>
        </div>
        <TrendCharts series={trend?.series} />
        <div className="csops-totals-grid">
          {[
            { label: 'Produced',      key: 'produced',    color: '#16a34a' },
            { label: 'Repack-In',     key: 'repack_in',   color: '#1565c0' },
            { label: 'Repack-Out',    key: 'repack_out',  color: '#ea580c' },
            { label: 'Reprint-Out',   key: 'reprint_out', color: '#be185d' },
            { label: 'Reprocess-Out', key: 'reproc_out',  color: '#7c3aed' },
            { label: 'Shipped',       key: 'shipped',     color: '#5b21b6' },
          ].map(t => (
            <div key={t.label} className="csops-total-tile"
                 style={{ background: t.color + '14', borderLeftColor: t.color }}>
              <div className="csops-total-tile-label" style={{ color: t.color }}>Total {t.label}</div>
              <div className="csops-total-tile-value" style={{ color: t.color }}>{fmtN(trend?.totals?.[t.key])}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
